import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { courseData } from '../data/courses';
import CourseCard from './CourseCard';
import CourseModal from './CourseModal';
import Pagination from './Pagination';
import { Search, Shield, Users, Car, Landmark } from 'lucide-react'; // Importamos iconos de Lucide
import { FaWhatsapp } from 'react-icons/fa'; // Importamos el icono de WhatsApp
import FilterDropdown from "./FilterDropDown";


// Objeto para mapear categorías a iconos de Lucide
const categoryIcons = {
  safety: Shield,
  workforce: Users,
  "txdot & environmental": Car,
  "CEU": Landmark,
};

// Object for map Filter Services
const options = [
  { value: "services", label: "Services" },
  { value: "training", label: "Training" }
];

const Courses = () => {
  const [selectedCategory, setSelectedCategory] = useState('workforce');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const itemsPerPage = 9; // Changed to 9 for a 3x3 grid on larger screens

  const [selectedServices, setSelectedServices] = useState("services");

  const categories = useMemo(() => Object.keys(courseData), []);

const filteredCourses = useMemo(() => {
  // Obtener los cursos según categoría y servicio seleccionado
  const courses = courseData[selectedCategory]?.[selectedServices] || [];
  // Filtrar cursos según término de búsqueda en nombre o descripción
  return courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description.toLowerCase().includes(searchTerm.toLowerCase())
  );
}, [selectedCategory, selectedServices, searchTerm]);

  const paginatedCourses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCourses.slice(startIndex, endIndex);
  }, [filteredCourses, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when category or search term changes
  }, [selectedCategory, searchTerm]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (course) => {
    setSelectedCourse(course);
  };

  const handleCloseModal = () => {
    setSelectedCourse(null);
  };

  return (
    <section id="courses" className="py-5 bg-gradient-to-br from-orange-50 via-orange-100 to-orange-50 relative overflow-hidden scroll-mt-40 ">
      <div className="">
        <div className="container mx-auto max-w-6xl">
          <motion.h1
            className="text-4xl font-extrabold text-center mb-4 bg-gradient-to-r from-orange-700 to-red-800 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className={`flex flex-wrap justify-center gap-3 mb- p-2 `}>
              <FilterDropdown
                value={selectedServices}
                onChange={setSelectedServices}
                options={options}
              />
            </div>
          </motion.h1>

          {/* Category Navigation */}
          <motion.nav
            className="flex flex-wrap justify-center gap-3 mb-8 p-2 bg-white/80 backdrop-blur-md rounded-full shadow-lg "
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >

            {categories.map((category) => {
              const IconComponent = categoryIcons[category]; // Obtener el componente del icono
              return (
                <motion.button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 ${selectedCategory === category
                    ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {IconComponent && <IconComponent className="w-6 h-6" />} {/* Renderizar el icono */}
                  {category.toUpperCase().replace(/&/g, ' & ')}
                </motion.button>
              );
            })}
          </motion.nav>

          {/* Search Bar and WhatsApp Button */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="relative w-full sm:w-auto flex-grow max-w-md">
              <input
                type="text"
                placeholder="Search course..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-6 py-3 pl-12 bg-white/90 border border-gray-200 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-300 text-gray-800 placeholder-gray-500"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <motion.a
              href="https://wa.me/19795755999?text=Hello!%20I'd%20like%20more%20information%20about%20your%20courses:%20"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaWhatsapp className="w-6 h-6" />
              I want information
            </motion.a>
          </motion.div>

          {/* Course Grid */}
          {paginatedCourses.length > 0 ? (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {paginatedCourses.map((course, index) => (
                <CourseCard
                  key={index}
                  course={course}
                  onDetailsClick={handleViewDetails}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              className="text-center text-gray-600 text-xl font-medium bg-white/80 rounded-2xl shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <p>Wow! It seems that there are no courses that match your search in this category.</p>
              <p className="mt-2">Maybe we should try something else?</p>
            </motion.div>
          )}

          {/* Pagination */}
          {filteredCourses.length > itemsPerPage && (
            <Pagination
              totalItems={filteredCourses.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}

          {/* Course Details Modal */}
          <CourseModal course={selectedCourse} onClose={handleCloseModal} />
        </div>
      </div>
    </section>
  );
};

export default Courses;